import { useEffect, useRef } from 'react';

/**
 * SmokeEmbers — the section above the hero.
 * Contains studio-title + WORLD + EKG heartbeat line.
 *
 * ALL styling is driven by props pulled from site settings (customize + theme).
 *
 * Props:
 *   studioTitle      string   "DUGOWTBOI ENTERTAINMENT™"
 *   studioTitleSize  number   px (default 40)
 *   studioTitleGradient string[]  array of hex stops
 *   worldText        string   "WORLD"
 *   worldSize        number   px (default 56)
 *   worldGradient    string[]
 *   ekgGradient      string[]  array of 2-4 hex stops for the line stroke
 *   ekgLineWidth     number   px (default 2)
 *   ekgRaysEnabled   bool     emit pulsating radial rays at cursor
 *   ekgRaysColor     string   hex
 *   ekgRaysIntensity number   0-100
 *   ekgSpeed         number   10-100 (cycle duration)
 */
export function SmokeEmbers({
  studioTitle = 'DUGOWTBOI ENTERTAINMENT™',
  studioTitleSize = 40,
  studioTitleGradient = ['#FF3B30', '#DAA520', '#FFFFFF', '#DAA520', '#FF3B30'],
  worldText = 'WORLD',
  worldSize = 56,
  worldGradient = ['#FF3B30', '#FF6347', '#DAA520', '#FFD700', '#FFFFFF'],
  ekgGradient = ['#FF3B30', '#DAA520', '#00F0FF'],
  ekgLineWidth = 2,
  ekgRaysEnabled = true,
  ekgRaysColor = '#FF3B30',
  ekgRaysIntensity = 70,
  ekgSpeed = 50,
}) {
  const hbCanvasRef = useRef(null);
  const worldRef = useRef(null);
  const rowRef = useRef(null);
  const hbAnimRef = useRef(null);
  const visibleRef = useRef(true);
  const propsRef = useRef({});

  // Keep latest props in a ref so the animation loop uses up-to-date values without restarting.
  propsRef.current = { ekgGradient, ekgLineWidth, ekgRaysEnabled, ekgRaysColor, ekgRaysIntensity, ekgSpeed };

  // Observe visibility — pause animation when offscreen
  useEffect(() => {
    const section = hbCanvasRef.current?.closest('section');
    if (!section) return;
    const observer = new IntersectionObserver(([entry]) => {
      visibleRef.current = entry.isIntersecting;
    }, { threshold: 0.1 });
    observer.observe(section);
    return () => observer.disconnect();
  }, []);

  // EKG heartbeat canvas with gradient stroke + optional pulsating radial rays
  useEffect(() => {
    const canvas = hbCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    const resize = () => { canvas.width = canvas.offsetWidth; canvas.height = canvas.offsetHeight; };
    resize();
    const W = () => canvas.width;
    const H = () => canvas.height;

    const ecg = (t) => {
      const a = H() * 0.42;
      if (t > 0.05 && t < 0.15) return -a * 0.12 * Math.sin((t - 0.05) / 0.10 * Math.PI);
      if (t > 0.21 && t < 0.24) return a * 0.06 * Math.sin((t - 0.21) / 0.03 * Math.PI);
      if (t > 0.24 && t < 0.28) return -a * 0.92 * Math.sin((t - 0.24) / 0.04 * Math.PI);
      if (t > 0.28 && t < 0.32) return a * 0.30 * Math.sin((t - 0.28) / 0.04 * Math.PI);
      if (t > 0.44 && t < 0.56) return -a * 0.18 * Math.sin((t - 0.44) / 0.12 * Math.PI);
      return 0;
    };

    const BPM = 60;
    const BEAT_MS = 60000 / BPM;
    const TRAIL_BEATS = 3;

    let startTime = null;
    let traceY = [];
    let prevCursorX = 0;

    const tick = (ts) => {
      if (!visibleRef.current) { hbAnimRef.current = requestAnimationFrame(tick); return; }
      const p = propsRef.current;
      // ekgSpeed 10-100 → cycle 18000..4000ms
      const CYCLE = 18000 - ((p.ekgSpeed - 10) / 90) * 14000;
      const SWEEP_FRAC = 0.62;
      const HOLD_FRAC = 0.13;
      const FADE_FRAC = 0.15;

      if (!startTime) startTime = ts;
      const elapsed = ts - startTime;
      const cycleTime = elapsed % CYCLE;
      const cycleFrac = cycleTime / CYCLE;

      const w = W();
      const h = H();
      const midY = h / 2;
      const sweepDuration = SWEEP_FRAC * CYCLE;
      const trailLen = Math.floor((TRAIL_BEATS * BEAT_MS / sweepDuration) * w);

      ctx.clearRect(0, 0, w, h);

      // Build gradient stroke based on ekgGradient array
      const makeStroke = (alpha) => {
        const stops = p.ekgGradient && p.ekgGradient.length ? p.ekgGradient : ['#FF3B30'];
        if (stops.length === 1) return stops[0];
        const grad = ctx.createLinearGradient(0, 0, w, 0);
        stops.forEach((c, i) => grad.addColorStop(i / (stops.length - 1), hexToRgba(c, alpha)));
        return grad;
      };

      const text = worldRef.current;
      const row = rowRef.current;

      if (cycleFrac < SWEEP_FRAC) {
        const sweepProgress = cycleFrac / SWEEP_FRAC;
        const cursorX = Math.floor(sweepProgress * w);

        for (let x = prevCursorX; x <= cursorX && x < w; x++) {
          const timeAtX = (x / w) * sweepDuration;
          const beatPhase = (timeAtX % BEAT_MS) / BEAT_MS;
          traceY[x] = midY + ecg(beatPhase);
        }
        prevCursorX = cursorX + 1;

        const tailX = Math.max(0, cursorX - trailLen);
        ctx.beginPath();
        ctx.strokeStyle = makeStroke(0.85);
        ctx.lineWidth = p.ekgLineWidth;
        ctx.lineJoin = 'round';
        ctx.lineCap = 'round';
        let started = false;
        for (let x = tailX; x <= cursorX && x < traceY.length; x++) {
          if (traceY[x] !== undefined) {
            if (!started) { ctx.moveTo(x, traceY[x]); started = true; }
            else ctx.lineTo(x, traceY[x]);
          }
        }
        ctx.stroke();

        // Bright dot at cursor
        const dotY = traceY[cursorX] || midY;

        // Pulsating rays from the cursor dot
        if (p.ekgRaysEnabled) {
          const intensity = (p.ekgRaysIntensity || 0) / 100;
          const pulse = 0.5 + 0.5 * Math.sin(ts * 0.01);
          const rayCount = 8;
          const maxLen = 30 + 50 * intensity * pulse;
          const [rr, rg, rb] = hexToRgbArr(p.ekgRaysColor);
          for (let i = 0; i < rayCount; i++) {
            const angle = (i / rayCount) * Math.PI * 2 + ts * 0.001;
            const len = maxLen * (0.7 + 0.3 * Math.sin(ts * 0.005 + i));
            const rayGrad = ctx.createLinearGradient(
              cursorX, dotY,
              cursorX + Math.cos(angle) * len,
              dotY + Math.sin(angle) * len
            );
            rayGrad.addColorStop(0, `rgba(${rr},${rg},${rb},${0.8 * intensity})`);
            rayGrad.addColorStop(1, `rgba(${rr},${rg},${rb},0)`);
            ctx.beginPath();
            ctx.moveTo(cursorX, dotY);
            ctx.lineTo(cursorX + Math.cos(angle) * len, dotY + Math.sin(angle) * len);
            ctx.strokeStyle = rayGrad;
            ctx.lineWidth = 1.5;
            ctx.stroke();
          }
          // Outer glow
          const glow = ctx.createRadialGradient(cursorX, dotY, 2, cursorX, dotY, 22 + 15 * pulse);
          glow.addColorStop(0, `rgba(${rr},${rg},${rb},${0.45 * intensity})`);
          glow.addColorStop(1, `rgba(${rr},${rg},${rb},0)`);
          ctx.fillStyle = glow;
          ctx.beginPath();
          ctx.arc(cursorX, dotY, 22 + 15 * pulse, 0, Math.PI * 2);
          ctx.fill();
        }

        // Central dot
        ctx.beginPath();
        ctx.arc(cursorX, dotY, 4, 0, Math.PI * 2);
        ctx.fillStyle = hexToRgba(p.ekgRaysColor, 1);
        ctx.fill();
        ctx.beginPath();
        ctx.arc(cursorX, dotY, 2, 0, Math.PI * 2);
        ctx.fillStyle = '#fff';
        ctx.fill();

        // Reveal WORLD text progressively
        if (text && row) {
          const rowRect = row.getBoundingClientRect();
          const textRect = text.getBoundingClientRect();
          const textL = textRect.left - rowRect.left;
          const textW = textRect.width || 1;
          const tailPct = Math.max(0, ((tailX - textL) / textW) * 100);
          const headPct = Math.min(100, ((cursorX - textL) / textW) * 100);
          const fadeZone = Math.min(20, (headPct - tailPct) * 0.3);
          text.style.clipPath = 'none';
          text.style.opacity = '1';
          text.style.webkitMaskImage = `linear-gradient(to right, transparent ${tailPct}%, rgba(0,0,0,1) ${tailPct + fadeZone}%, rgba(0,0,0,1) ${headPct}%, transparent ${headPct}%)`;
          text.style.maskImage = `linear-gradient(to right, transparent ${tailPct}%, rgba(0,0,0,1) ${tailPct + fadeZone}%, rgba(0,0,0,1) ${headPct}%, transparent ${headPct}%)`;
        }

      } else if (cycleFrac < SWEEP_FRAC + HOLD_FRAC) {
        const holdProgress = (cycleFrac - SWEEP_FRAC) / HOLD_FRAC;
        const fadeLen = Math.max(0, trailLen - Math.floor(holdProgress * trailLen));
        const lastX = traceY.length - 1;
        const tailX = Math.max(0, lastX - fadeLen);
        drawSimpleTrail(ctx, traceY, tailX, lastX, 1 - holdProgress * 0.5, makeStroke(1 - holdProgress * 0.5), p.ekgLineWidth);
        if (text && row) {
          const textRect = text.getBoundingClientRect();
          const rowRect = row.getBoundingClientRect();
          const textL = textRect.left - rowRect.left;
          const textW = textRect.width || 1;
          const tailPct = Math.max(0, ((tailX - textL) / textW) * 100);
          text.style.opacity = '1';
          text.style.webkitMaskImage = `linear-gradient(to right, transparent ${tailPct}%, rgba(0,0,0,1) ${tailPct + 15}%, rgba(0,0,0,1) 100%)`;
          text.style.maskImage = `linear-gradient(to right, transparent ${tailPct}%, rgba(0,0,0,1) ${tailPct + 15}%, rgba(0,0,0,1) 100%)`;
        }
      } else if (cycleFrac < SWEEP_FRAC + HOLD_FRAC + FADE_FRAC) {
        const fadeAlpha = 1 - (cycleFrac - SWEEP_FRAC - HOLD_FRAC) / FADE_FRAC;
        const remaining = Math.floor(trailLen * 0.3 * fadeAlpha);
        const lastX = traceY.length - 1;
        const tailX = Math.max(0, lastX - remaining);
        if (remaining > 5) drawSimpleTrail(ctx, traceY, tailX, lastX, fadeAlpha * 0.5, makeStroke(fadeAlpha * 0.5), p.ekgLineWidth);
        if (text) text.style.opacity = `${fadeAlpha}`;
      } else {
        traceY = [];
        prevCursorX = 0;
        if (text) {
          text.style.opacity = '0';
          text.style.webkitMaskImage = 'none';
          text.style.maskImage = 'none';
          text.style.clipPath = 'inset(0 100% 0 0)';
        }
      }

      hbAnimRef.current = requestAnimationFrame(tick);
    };

    function drawSimpleTrail(c, trace, fromX, toX, alpha, strokeStyle, lineWidth) {
      c.beginPath();
      c.strokeStyle = strokeStyle;
      c.lineWidth = lineWidth;
      c.lineJoin = 'round';
      c.lineCap = 'round';
      let started = false;
      for (let x = fromX; x <= toX && x < trace.length; x++) {
        if (trace[x] !== undefined) {
          if (!started) { c.moveTo(x, trace[x]); started = true; }
          else c.lineTo(x, trace[x]);
        }
      }
      c.stroke();
    }

    hbAnimRef.current = requestAnimationFrame(tick);
    const handleResize = () => { traceY = []; prevCursorX = 0; resize(); };
    window.addEventListener('resize', handleResize);
    return () => { cancelAnimationFrame(hbAnimRef.current); window.removeEventListener('resize', handleResize); };
  }, []);

  const studioGradientCss = studioTitleGradient && studioTitleGradient.length
    ? `linear-gradient(135deg, ${studioTitleGradient.join(', ')})`
    : `linear-gradient(135deg, #FF3B30, #DAA520, #fff)`;

  const worldGradientCss = worldGradient && worldGradient.length
    ? `linear-gradient(90deg, ${worldGradient.join(', ')})`
    : `linear-gradient(90deg, #FF3B30, #DAA520, #FFF)`;

  return (
    <section className="relative overflow-hidden bg-[#050505] border-b border-[#1a1a1a]" data-testid="smoke-embers-section" style={{ height: '280px' }}>
      <div className="relative z-10 flex flex-col items-center justify-center h-full">
        <h2
          className="studio-name font-bold uppercase tracking-[0.2em] text-center px-4"
          style={{
            fontFamily: "var(--font-display, 'Orbitron', sans-serif)",
            fontSize: `${studioTitleSize}px`,
            lineHeight: 1.1,
            backgroundImage: studioGradientCss,
            backgroundSize: '200% 200%',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            color: 'transparent',
            animation: 'shimmer 4s ease-in-out infinite',
            filter: 'drop-shadow(0 0 20px rgba(218, 165, 32, 0.3))'
          }}
          data-testid="studio-name"
        >
          {studioTitle}
        </h2>

        <div ref={rowRef} className="w-full relative flex justify-center mt-2" style={{ minHeight: `${worldSize + 40}px` }}>
          <span
            ref={worldRef}
            className="world-text uppercase tracking-[0.25em]"
            style={{
              fontFamily: "var(--font-display, 'Anton', sans-serif)",
              fontWeight: 400,
              clipPath: 'inset(0 100% 0 0)',
              opacity: 0,
              fontSize: `${worldSize}px`,
              lineHeight: 1,
              display: 'inline-block',
              backgroundImage: worldGradientCss,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              color: 'transparent',
              filter: 'drop-shadow(0 0 25px rgba(255, 59, 48, 0.4))'
            }}
            data-testid="world-text"
          >
            {worldText}
          </span>
          <canvas
            ref={hbCanvasRef}
            className="absolute inset-0 w-full h-full pointer-events-none"
            style={{ zIndex: 20 }}
          />
        </div>
      </div>

      <style>{`
        @keyframes shimmer {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
      `}</style>
    </section>
  );
}

// helpers
function hexToRgbArr(hex, fb = [255, 59, 48]) {
  if (!hex) return fb;
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex.trim());
  return m ? [parseInt(m[1], 16), parseInt(m[2], 16), parseInt(m[3], 16)] : fb;
}
function hexToRgba(hex, alpha = 1) {
  const [r, g, b] = hexToRgbArr(hex);
  return `rgba(${r},${g},${b},${alpha})`;
}
